import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  FileSignature,
  AlertTriangle,
  CheckCircle2,
  Download,
  Settings,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Button } from '@/ui/base/button';
import { Badge } from '@/ui/base/badge';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { useGovernancaInstitucional } from './useGovernancaInstitucional';
import { exportGovernancaInstitucionalPDF } from './exportGovernancaInstitucionalPDF';
import { toast } from 'sonner';
import { PageHeader } from '@/ui/PageHeader';
import { Progress } from '@/ui/base/progress';
import { Spinner } from '@/ui/Spinner';

const ROTULOS_OPERACAO: Record<string, string> = {
  login_portal_credenciado: 'Login Portal Credenciado',
  login_portal_admin: 'Login Portal Admin',
  assinatura_termo: 'Assinatura de Termo',
  assinatura_aditivo: 'Assinatura de Aditivo',
  assinatura_distrato: 'Assinatura de Distrato',
  inscricao_publica: 'Inscrição Pública',
  consulta_publica: 'Consulta Pública',
};

const ROTULOS_TIPO_DOC: Record<string, string> = {
  termo: 'Termo de Adesão',
  aditivo: 'Aditivo',
  distrato: 'Distrato',
  contrato: 'Contrato',
  declaracao: 'Declaração',
  parecer: 'Parecer',
};

const SELO_VARIANT: Record<string, 'secondary' | 'default' | 'outline'> = {
  bronze: 'outline',
  prata: 'secondary',
  ouro: 'default',
};

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'default',
}: {
  icon: typeof ShieldCheck;
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const toneClasses: Record<string, string> = {
    default: 'text-primary bg-primary/10',
    success: 'text-success bg-success/10',
    warning: 'text-warning bg-warning/10',
    danger: 'text-destructive bg-destructive/10',
  };
  return (
    <Card>
      <CardContent className="p-5 flex items-start gap-4">
        <div className={`rounded-xl p-3 ${toneClasses[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-semibold mt-1">{value}</div>
          {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function GovernancaInstitucionalPage() {
  const { data, isLoading, isError, refetch, isFetching } = useGovernancaInstitucional();

  const integridadeTone = useMemo<'success' | 'warning' | 'danger'>(() => {
    const pct = data?.kpis.integridade_pct ?? 100;
    if (pct >= 99) return 'success';
    if (pct >= 90) return 'warning';
    return 'danger';
  }, [data]);

  function handleExport() {
    if (!data) return;
    try {
      exportGovernancaInstitucionalPDF(data);
      toast.success('PDF de Política Institucional gerado');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao gerar PDF');
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Governança Institucional"
        description="Conformidade Gov.BR e assinatura digital — visão consolidada para auditoria SME/CGM"
      >
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/parametros/govbr-operacoes">
              <Settings className="h-4 w-4 mr-2" />
              Parametrizar Gov.BR
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/parametros/politica-assinatura">
              <Settings className="h-4 w-4 mr-2" />
              Política de assinatura
            </Link>
          </Button>
          <Button size="sm" onClick={handleExport} disabled={!data}>
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>
        </div>
      </PageHeader>

      {isLoading && (
        <div className="py-20 flex justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {isError && (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            Falha ao carregar indicadores de governança. Verifique se você possui perfil
            ADMIN/GESTOR_ADMIN.
          </CardContent>
        </Card>
      )}
      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={ShieldCheck}
              label="Cobertura Gov.BR"
              value={`${data.kpis.cobertura_govbr_pct}%`}
              hint={`${data.kpis.inscricoes_govbr} de ${data.kpis.total_inscricoes} inscrições`}
              tone={data.kpis.cobertura_govbr_pct >= 70 ? 'success' : 'warning'}
            />
            <StatCard
              icon={FileSignature}
              label="Termos com assinatura"
              value={`${data.kpis.cobertura_assinatura_pct}%`}
              hint={`${data.kpis.termos_com_assinatura} de ${data.kpis.total_termos_ativos} termos ATIVO/VIGENTE`}
              tone={data.kpis.cobertura_assinatura_pct >= 80 ? 'success' : 'warning'}
            />
            <StatCard
              icon={CheckCircle2}
              label="Assinaturas registradas"
              value={String(data.kpis.total_assinaturas)}
              hint="Trilha imutável SHA-256"
            />
            <StatCard
              icon={integridadeTone === 'danger' ? AlertTriangle : CheckCircle2}
              label="Integridade hash chain"
              value={`${data.kpis.integridade_pct}%`}
              hint={
                data.integridade.termos_quebrados > 0
                  ? `${data.integridade.termos_quebrados} termo(s) com cadeia quebrada`
                  : 'Sem quebras detectadas'
              }
              tone={integridadeTone}
            />
          </div>

          {/* Cobertura Gov.BR por operação */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                Cobertura Gov.BR por operação
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Operação</TableHead>
                    <TableHead>Selo mínimo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Descrição</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.govbr_cobertura.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                        Nenhuma operação parametrizada
                      </TableCell>
                    </TableRow>
                  )}
                  {data.govbr_cobertura.map((c) => (
                    <TableRow key={c.operacao}>
                      <TableCell className="font-medium">
                        {ROTULOS_OPERACAO[c.operacao] ?? c.operacao}
                      </TableCell>
                      <TableCell>
                        <Badge variant={SELO_VARIANT[c.selo_minimo]}>
                          {c.selo_minimo.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {c.ativo ? (
                          <Badge variant="outline" className="border-success/50 text-success">
                            Ativo
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Inativo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {c.descricao ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Política de assinatura */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileSignature className="h-4 w-4" />
                Política de assinatura por tipo de documento
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Nível mínimo</TableHead>
                    <TableHead>Selo Gov.BR</TableHead>
                    <TableHead>Provedores aceitos</TableHead>
                    <TableHead>Base legal</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.politica.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                        Nenhuma política cadastrada
                      </TableCell>
                    </TableRow>
                  )}
                  {data.politica.map((p) => (
                    <TableRow key={p.tipo_documento}>
                      <TableCell className="font-medium">
                        {ROTULOS_TIPO_DOC[p.tipo_documento] ?? p.tipo_documento}
                      </TableCell>
                      <TableCell className="capitalize">{p.nivel_minimo}</TableCell>
                      <TableCell>
                        {p.selo_govbr_minimo ? (
                          <Badge variant={SELO_VARIANT[p.selo_govbr_minimo]}>
                            {p.selo_govbr_minimo.toUpperCase()}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {(p.provedores ?? [])
                          .filter((pr) => pr.ativo)
                          .map((pr) => pr.provedor)
                          .join(', ') || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[280px]">
                        {p.base_legal}
                      </TableCell>
                      <TableCell>
                        {p.ativo ? (
                          <Badge variant="outline" className="border-success/50 text-success">
                            Ativo
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Inativo</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Cobertura por DRE */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cobertura de assinaturas por DRE</CardTitle>
            </CardHeader>
            <CardContent>
              {data.termos_por_dre.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhum termo ATIVO/VIGENTE para análise.
                </p>
              ) : (
                <div className="space-y-4">
                  {data.termos_por_dre.map((d) => (
                    <div key={d.dre} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{d.dre}</span>
                        <span className="text-muted-foreground">
                          {d.com_assinatura}/{d.total} ({d.percentual}%)
                        </span>
                      </div>
                      <Progress value={d.percentual} className="h-2" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Integridade detalhada */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                Integridade da trilha imutável (SHA-256)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground text-xs">Assinaturas</div>
                  <div className="text-lg font-semibold">{data.integridade.total_assinaturas}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Termos íntegros</div>
                  <div className="text-lg font-semibold text-success">
                    {data.integridade.termos_integros}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Termos quebrados</div>
                  <div
                    className={`text-lg font-semibold ${
                      data.integridade.termos_quebrados > 0 ? 'text-destructive' : ''
                    }`}
                  >
                    {data.integridade.termos_quebrados}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Cadeias quebradas</div>
                  <div
                    className={`text-lg font-semibold ${
                      data.integridade.cadeias_quebradas > 0 ? 'text-destructive' : ''
                    }`}
                  >
                    {data.integridade.cadeias_quebradas}
                  </div>
                </div>
              </div>
              {data.integridade.termos_quebrados > 0 && (
                <div className="mt-4 rounded-lg bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive flex gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    Quebras detectadas na hash chain. Acesse o detalhe dos termos afetados para
                    investigação imediata e abertura de incidente.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground">
            Indicadores gerados em {new Date(data.gerado_em).toLocaleString('pt-BR')}
          </p>
        </>
      )}
    </div>
  );
}
